import React, { useEffect, useRef, useState } from 'react';
import Konva from 'konva';
import { FieldTransform, FieldOrientation } from './FieldCanvas';
import { PlacedBunkerState } from '../lib/layoutState';
import { BunkerDef, TilesetKey, bunkerDisplayName } from '../lib/bunkerCatalog';
import {
  stageCoordsToField,
  fieldToStageCoords,
  snapToGrid,
  clampField,
  pointerToStageCoords,
  mirrorRotation,
} from '../lib/fieldCoords';

// Default ghost rotation when a bunker type is first selected.
// Horizontal layout: long axis runs left-right, so elongated/wide bunkers need 90°.
// Vertical layout: 0° (sprites are oriented for vertical by default).
const HORIZONTAL_DEFAULT_ROT: Record<string, number> = {
  SNAKE_BEAM: 90, A: 90, W: 90, MINI_A: 90, MINI_W: 90, MINI_X: 90, X: 90,
  BRICK: 90, BRICK2022_UPRIGHT: 90, WING2022: 90,
  // GIANT_WING taper is on local +X; needs 90° so local +X faces canvas-down.
  GIANT_WING: 90, GIANT_WING_FLAT: 270, GIANT_BRICK: 90, BOX: 90, SMALL_BOX: 90,
  // GIANT_WING_FLAT and WING2022_UPRIGHT taper is on local -X; needs 270° to face canvas-down.
  WING2022_UPRIGHT: 270,
  TORIDO: 270, SMALL_TORIDO: 270, CAKE: 270, TALL_CAKE: 270,
};

interface BunkerLayerProps {
  stage: Konva.Stage;
  transform: FieldTransform;
  placedBunkers: PlacedBunkerState[];
  bunkerDefs: Record<string, BunkerDef>;
  tileset: TilesetKey;
  spriteImages: Record<string, HTMLImageElement>;
  orientation: FieldOrientation;
  selectedType: string | null;
  snapFt: number;
  rotSnapDeg: number;
  mirrorEnabled: boolean;
  onPlace:      (b: Omit<PlacedBunkerState, 'id'>) => string;
  onUpdate:     (id: string, updates: Partial<Omit<PlacedBunkerState, 'id'>>) => void;
  onRemove:     (id: string) => void;
  onRemoveMany: (ids: string[]) => void;
  onExitPlacement: () => void;
}

// dragBoundFunc uses absolute (screen-pixel) coordinates; converts through field feet.
function makeDragBound(
  stage: Konva.Stage,
  transformRef: React.MutableRefObject<FieldTransform>,
  snapFtRef: React.MutableRefObject<number>,
) {
  return (absPos: { x: number; y: number }) => {
    const s = stage;
    const t = transformRef.current;
    const lx = (absPos.x - s.x()) / s.scaleX();
    const ly = (absPos.y - s.y()) / s.scaleY();
    let { xFt, zFt } = stageCoordsToField(lx, ly, t);
    const sf = snapFtRef.current;
    if (sf > 0) { xFt = snapToGrid(xFt, sf); zFt = snapToGrid(zFt, sf); }
    const c = clampField(xFt, zFt);
    const { x: cx, y: cy } = fieldToStageCoords(c.xFt, c.zFt, t);
    return { x: cx * s.scaleX() + s.x(), y: cy * s.scaleY() + s.y() };
  };
}

function buildGhostGroup(
  widthFt: number,
  depthFt: number,
  rotation: number,
  basePxPerFoot: number,
  img?: HTMLImageElement,
  opacity = 0.6,
): Konva.Group {
  const pxW   = widthFt * basePxPerFoot;
  const pxH   = depthFt * basePxPerFoot;
  const group = new Konva.Group({ x: -9999, y: -9999, rotation, listening: false, opacity });
  if (img) {
    group.add(new Konva.Image({ image: img, x: -pxW / 2, y: -pxH / 2, width: pxW, height: pxH }));
  } else {
    group.add(new Konva.Rect({
      x: -pxW / 2, y: -pxH / 2, width: pxW, height: pxH,
      fill: '#0095da', stroke: '#ffffff', strokeWidth: 1.5, dash: [4, 3],
    }));
  }
  return group;
}

const CENTERLINE_TOLERANCE_FT = 1;

interface DragTarget {
  group: Konva.Group;
  startX: number;
  startY: number;
}

export const BunkerLayer: React.FC<BunkerLayerProps> = ({
  stage, transform, placedBunkers, bunkerDefs,
  tileset, spriteImages,
  orientation, selectedType, snapFt, rotSnapDeg, mirrorEnabled,
  onPlace, onUpdate, onRemove, onRemoveMany, onExitPlacement,
}) => {
  // Multi-select — array of IDs
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const placedLayerRef    = useRef<Konva.Layer | null>(null);
  const labelsLayerRef    = useRef<Konva.Layer | null>(null);
  const ghostLayerRef     = useRef<Konva.Layer | null>(null);
  const ghostGroupRef     = useRef<Konva.Group | null>(null);
  const mirrorGhostRef    = useRef<Konva.Group | null>(null);
  const ghostPosRef       = useRef<{ xFt: number; zFt: number } | null>(null);
  const rotByTypeRef      = useRef<Record<string, number>>({});
  const clipboardRef             = useRef<Omit<PlacedBunkerState, 'id'>[]>([]);
  const labelsByIdRef            = useRef<Map<string, { node: Konva.Text; halfH: number }>>(new Map());
  const justCompletedDragSelect = useRef(false);
  // Drag-select state
  const dragSelectStartRef = useRef<{ x: number; y: number } | null>(null);
  const isDragSelectRef    = useRef(false);
  const selRectRef         = useRef<Konva.Rect | null>(null);
  // Multi-group drag state
  const dragAnchorRef       = useRef<{ x: number; y: number } | null>(null);
  const otherDragTargets    = useRef<DragTarget[]>([]);

  // Stable refs — always current, safe to read inside event-handler closures
  const transformRef     = useRef(transform);
  const selectedTypeRef  = useRef(selectedType);
  const selectedIdsRef   = useRef<string[]>([]);
  const snapFtRef        = useRef(snapFt);
  const rotSnapDegRef    = useRef(rotSnapDeg);
  const mirrorEnabledRef = useRef(mirrorEnabled);
  const onPlaceRef       = useRef(onPlace);
  const onUpdateRef      = useRef(onUpdate);
  const onRemoveRef      = useRef(onRemove);
  const onRemoveManyRef  = useRef(onRemoveMany);
  const onExitRef        = useRef(onExitPlacement);
  const bunkerDefsRef    = useRef(bunkerDefs);
  const placedBunkersRef = useRef(placedBunkers);
  const tilesetRef        = useRef(tileset);
  const spriteImagesRef   = useRef(spriteImages);
  const orientationRef    = useRef(orientation);
  const prevSelectedTypeRef = useRef<string | null>(null);

  useEffect(() => { transformRef.current     = transform;        }, [transform]);
  useEffect(() => { selectedTypeRef.current  = selectedType;     }, [selectedType]);
  useEffect(() => { snapFtRef.current        = snapFt;           }, [snapFt]);
  useEffect(() => { rotSnapDegRef.current    = rotSnapDeg;       }, [rotSnapDeg]);
  useEffect(() => { mirrorEnabledRef.current = mirrorEnabled;    }, [mirrorEnabled]);
  useEffect(() => { onPlaceRef.current       = onPlace;          }, [onPlace]);
  useEffect(() => { onUpdateRef.current      = onUpdate;         }, [onUpdate]);
  useEffect(() => { onRemoveRef.current      = onRemove;         }, [onRemove]);
  useEffect(() => { onRemoveManyRef.current  = onRemoveMany;     }, [onRemoveMany]);
  useEffect(() => { onExitRef.current        = onExitPlacement;  }, [onExitPlacement]);
  useEffect(() => { bunkerDefsRef.current    = bunkerDefs;       }, [bunkerDefs]);
  useEffect(() => { placedBunkersRef.current = placedBunkers;    }, [placedBunkers]);
  useEffect(() => { tilesetRef.current        = tileset;       }, [tileset]);
  useEffect(() => { spriteImagesRef.current  = spriteImages;  }, [spriteImages]);
  useEffect(() => { orientationRef.current   = orientation;   }, [orientation]);

  const setSelection = (ids: string[]) => {
    selectedIdsRef.current = ids;
    setSelectedIds(ids);
  };

  // ── Create layers & bind all stage/keyboard events ──────────────────────────
  useEffect(() => {
    const placedLayer = new Konva.Layer();
    const labelsLayer = new Konva.Layer({ listening: false });
    const ghostLayer  = new Konva.Layer({ listening: false });
    placedLayer.name('bunkers');
    labelsLayer.name('bunker-labels');
    ghostLayer.name('ghost');
    stage.add(placedLayer);
    stage.add(labelsLayer);
    stage.add(ghostLayer);
    placedLayerRef.current = placedLayer;
    labelsLayerRef.current = labelsLayer;
    ghostLayerRef.current  = ghostLayer;

    // Selection-rectangle shape lives in the ghost layer
    const selRect = new Konva.Rect({
      x: 0, y: 0, width: 0, height: 0,
      stroke: '#0095da', strokeWidth: 1,
      dash: [5, 3],
      fill: 'rgba(0,149,218,0.08)',
      visible: false,
      listening: false,
    });
    ghostLayer.add(selRect);
    selRectRef.current = selRect;

    const spaceHeld = { value: false };

    // ── Keyboard ────────────────────────────────────────────────────────────
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space') { spaceHeld.value = true; return; }

      // Ctrl / Cmd shortcuts — skip when a form field is focused
      if (e.ctrlKey || e.metaKey) {
        const tag = (document.activeElement as HTMLElement)?.tagName ?? '';
        if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;

        if (e.code === 'KeyC') {
          const ids = selectedIdsRef.current;
          if (ids.length > 0) {
            clipboardRef.current = placedBunkersRef.current
              .filter(b => ids.includes(b.id))
              .map(({ id: _id, ...rest }) => rest);
          }
          e.preventDefault();
          return;
        }

        if (e.code === 'KeyV') {
          const clipboard = clipboardRef.current;
          if (clipboard.length === 0) return;
          e.preventDefault();
          const newIds: string[] = [];
          for (const b of clipboard) {
            const clamped = clampField(b.xFt + 5, b.zFt + 5);
            const id = onPlaceRef.current({ ...b, xFt: clamped.xFt, zFt: clamped.zFt });
            newIds.push(id);
          }
          setSelection(newIds);
          return;
        }
        return;
      }

      const selIds  = selectedIdsRef.current;
      const selType = selectedTypeRef.current;
      const rotStep = rotSnapDegRef.current === 0 ? 5 : rotSnapDegRef.current;

      if (e.code === 'ArrowLeft' || e.code === 'ArrowRight' ||
          e.code === 'ArrowUp'   || e.code === 'ArrowDown') {
        if (selIds.length === 0) return;
        const tag = (document.activeElement as HTMLElement)?.tagName ?? '';
        if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
        e.preventDefault();
        let dxFt = 0, dzFt = 0;
        if (orientationRef.current === 'horizontal') {
          // canvas-X = game-Z, canvas-Y = game-X
          if (e.code === 'ArrowLeft')  dzFt = -1;
          if (e.code === 'ArrowRight') dzFt =  1;
          if (e.code === 'ArrowUp')    dxFt = -1;
          if (e.code === 'ArrowDown')  dxFt =  1;
        } else {
          // canvas-X = game-X, canvas-Y = game-Z
          if (e.code === 'ArrowLeft')  dxFt = -1;
          if (e.code === 'ArrowRight') dxFt =  1;
          if (e.code === 'ArrowUp')    dzFt = -1;
          if (e.code === 'ArrowDown')  dzFt =  1;
        }
        for (const id of selIds) {
          const b = placedBunkersRef.current.find(x => x.id === id);
          if (b) onUpdateRef.current(id, clampField(b.xFt + dxFt, b.zFt + dzFt));
        }
        return;
      }

      if (e.code === 'KeyR') {
        if (selIds.length > 0) {
          // Rotate all selected placed bunkers
          for (const id of selIds) {
            const b = placedBunkersRef.current.find(x => x.id === id);
            if (b) onUpdateRef.current(id, { rotationDeg: (b.rotationDeg + rotStep) % 360 });
          }
        } else if (selType) {
          // Rotate placement ghost
          const cur  = rotByTypeRef.current[selType] ?? 0;
          const next = (cur + rotStep) % 360;
          rotByTypeRef.current[selType] = next;
          const g  = ghostGroupRef.current;
          const mg = mirrorGhostRef.current;
          if (g && ghostLayerRef.current) {
            g.rotation(next);
            if (mg) {
              const def    = bunkerDefsRef.current[selType];
              const mirRot = def?.taperEdge ? next : mirrorRotation(next, orientationRef.current);
              mg.rotation(mirRot);
            }
            ghostLayerRef.current.batchDraw();
          }
        }
        return;
      }

      if (e.code === 'Delete' || e.code === 'Backspace') {
        if (selIds.length > 0) {
          onRemoveManyRef.current(selIds);
          setSelection([]);
        }
        return;
      }

      if (e.code === 'Escape') {
        if (selIds.length > 0)  setSelection([]);
        else if (selType)       onExitRef.current();
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') spaceHeld.value = false;
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup',   onKeyUp);

    // ── Drag-select start (background mousedown only) ────────────────────────
    stage.on('mousedown.bunker', (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (e.evt.button !== 0 || spaceHeld.value) return;
      if (selectedTypeRef.current) return; // placement mode; ignore
      // Only trigger when NOT clicking on a placed bunker.
      // Checking bunkerId is more reliable than e.target === stage because
      // Konva may return a Layer node (not Stage) for empty-area clicks.
      const target = e.target as Konva.Node;
      const hasBunkerId = target.getAttr('bunkerId') || target.parent?.getAttr('bunkerId');
      if (hasBunkerId) return;

      const sc = pointerToStageCoords(stage);
      if (!sc) return;
      dragSelectStartRef.current = { ...sc };
      isDragSelectRef.current    = false;
    });

    // ── Mouse move: ghost + drag-select rect ─────────────────────────────────
    stage.on('mousemove.bunker', () => {
      const sc = pointerToStageCoords(stage);
      if (!sc) return;
      const t  = transformRef.current;
      const sf = snapFtRef.current;

      // Placement ghost
      const selType = selectedTypeRef.current;
      const g       = ghostGroupRef.current;
      if (selType && g) {
        let { xFt, zFt } = stageCoordsToField(sc.x, sc.y, t);
        if (sf > 0) { xFt = snapToGrid(xFt, sf); zFt = snapToGrid(zFt, sf); }
        const clamped = clampField(xFt, zFt);
        ghostPosRef.current = clamped;

        const { x, y } = fieldToStageCoords(clamped.xFt, clamped.zFt, t);
        g.position({ x, y });

        const mg = mirrorGhostRef.current;
        if (mg) {
          const nearCenter = Math.abs(clamped.zFt) < CENTERLINE_TOLERANCE_FT;
          if (mirrorEnabledRef.current && !nearCenter) {
            const rot    = rotByTypeRef.current[selType] ?? 0;
            const def    = bunkerDefsRef.current[selType];
            const mirRot = def?.taperEdge ? rot : mirrorRotation(rot, orientationRef.current);
            const { x: mx, y: my } = fieldToStageCoords(clamped.xFt, -clamped.zFt, t);
            mg.position({ x: mx, y: my });
            mg.rotation(mirRot);
            mg.visible(true);
          } else {
            mg.visible(false);
          }
        }

        ghostLayerRef.current?.batchDraw();
      }

      // Drag-select rectangle
      const start = dragSelectStartRef.current;
      if (start && !selType) {
        const dx = sc.x - start.x;
        const dy = sc.y - start.y;
        if (!isDragSelectRef.current && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
          isDragSelectRef.current = true;
          selRectRef.current?.visible(true);
        }
        if (isDragSelectRef.current) {
          selRectRef.current?.setAttrs({
            x: Math.min(start.x, sc.x),
            y: Math.min(start.y, sc.y),
            width:  Math.abs(dx),
            height: Math.abs(dy),
          });
          ghostLayerRef.current?.batchDraw();
        }
      }
    });

    // ── Mouse up: complete drag-select ───────────────────────────────────────
    stage.on('mouseup.bunker', () => {
      if (!isDragSelectRef.current) {
        dragSelectStartRef.current = null;
        return;
      }

      const rect = selRectRef.current;
      isDragSelectRef.current    = false;
      dragSelectStartRef.current = null;

      if (rect) {
        const rx1 = rect.x();
        const ry1 = rect.y();
        const rx2 = rx1 + rect.width();
        const ry2 = ry1 + rect.height();

        const hit: string[] = [];
        const t = transformRef.current;
        for (const b of placedBunkersRef.current) {
          const { x, y } = fieldToStageCoords(b.xFt, b.zFt, t);
          if (x >= rx1 && x <= rx2 && y >= ry1 && y <= ry2) hit.push(b.id);
        }
        setSelection(hit);
        justCompletedDragSelect.current = true;

        rect.visible(false);
        ghostLayerRef.current?.batchDraw();
      }
    });

    // ── Stage click: place or deselect ───────────────────────────────────────
    stage.on('click.bunker', (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (e.evt.button !== 0 || spaceHeld.value) return;
      // Bunker group clicks cancel bubbling, so reaching here means background
      const selType = selectedTypeRef.current;
      const pos     = ghostPosRef.current;

      if (selType && pos) {
        const rot = rotByTypeRef.current[selType] ?? 0;
        onPlaceRef.current({ type: selType, xFt: pos.xFt, zFt: pos.zFt, rotationDeg: rot });
        const nearCenter = Math.abs(pos.zFt) < CENTERLINE_TOLERANCE_FT;
        if (mirrorEnabledRef.current && !nearCenter) {
          const def    = bunkerDefsRef.current[selType];
          const mirRot = def?.taperEdge ? rot : mirrorRotation(rot, orientationRef.current);
          onPlaceRef.current({
            type: selType, xFt: pos.xFt, zFt: -pos.zFt,
            rotationDeg: mirRot,
          });
        }
      } else if (!isDragSelectRef.current) {
        if (justCompletedDragSelect.current) {
          justCompletedDragSelect.current = false;
        } else {
          setSelection([]);
        }
      }
    });

    return () => {
      stage.off('mousedown.bunker');
      stage.off('mousemove.bunker');
      stage.off('mouseup.bunker');
      stage.off('click.bunker');
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup',   onKeyUp);
      placedLayer.destroy();
      labelsLayer.destroy();
      ghostLayer.destroy();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // ── Redraw placed bunkers ────────────────────────────────────────────────────
  useEffect(() => {
    const layer = placedLayerRef.current;
    if (!layer) return;
    layer.destroyChildren();

    const t     = transform;
    const ppf   = t.basePxPerFoot;
    const bound = makeDragBound(stage, transformRef, snapFtRef);
    const selSet = new Set(selectedIds);

    for (const b of placedBunkers) {
      const def = bunkerDefs[b.type];
      if (!def) continue;

      const { x, y } = fieldToStageCoords(b.xFt, b.zFt, t);
      const sprite    = def.sprites?.[tileset];
      const resolvedW = sprite?.widthFt ?? def.widthFt;
      const resolvedD = sprite?.depthFt ?? def.depthFt;
      const img       = spriteImages[b.type];
      const pxW   = resolvedW * ppf;
      const pxH   = resolvedD * ppf;
      const isSel = selSet.has(b.id);

      const group = new Konva.Group({
        x, y,
        rotation: b.rotationDeg,
        draggable: true,
        dragBoundFunc: bound,
      });
      group.setAttr('bunkerId', b.id);

      if (img) {
        group.add(new Konva.Image({ image: img, x: -pxW / 2, y: -pxH / 2, width: pxW, height: pxH }));
        group.add(new Konva.Rect({
          x: -pxW / 2, y: -pxH / 2, width: pxW, height: pxH,
          fill:        isSel ? 'rgba(0,149,218,0.35)' : 'transparent',
          stroke:      isSel ? '#ffcc00' : 'rgba(255,255,255,0.25)',
          strokeWidth: isSel ? 2.5 : 0.5,
        }));
      } else {
        group.add(new Konva.Rect({
          x: -pxW / 2, y: -pxH / 2, width: pxW, height: pxH,
          fill:        isSel ? '#1a7abf' : '#2060a8',
          stroke:      isSel ? '#ffcc00' : '#ffffff',
          strokeWidth: isSel ? 2.5 : 1,
          opacity: 0.9,
        }));
      }

      // Taper indicator for wing bunkers: full-height bar on the long (non-tapered) side,
      // short centered bar on the tapered side.
      if (def.taperEdge) {
        const sw        = Math.max(3, Math.min(10, ppf * 0.7));
        const taperX    = def.taperEdge === 'left' ? -pxW / 2 : pxW / 2 - sw;
        const nonTaperX = def.taperEdge === 'left' ? pxW / 2 - sw : -pxW / 2;
        const shortH    = pxH * 0.4;
        const base = { fill: '#ffaa00', opacity: 0.85, listening: false, width: sw };
        group.add(new Konva.Rect({ ...base, x: nonTaperX, y: -pxH / 2, height: pxH }));
        group.add(new Konva.Rect({ ...base, x: taperX,    y: -shortH / 2, height: shortH }));
      }

      // Click: select (Shift adds to selection)
      group.on('click', (e: Konva.KonvaEventObject<MouseEvent>) => {
        if (e.evt.button !== 0) return;
        e.cancelBubble = true;
        if (e.evt.shiftKey) {
          const cur = selectedIdsRef.current;
          setSelection(cur.includes(b.id)
            ? cur.filter(id => id !== b.id)
            : [...cur, b.id]);
        } else {
          setSelection([b.id]);
          if (selectedTypeRef.current) onExitRef.current();
        }
      });

      // Drag: co-move all selected bunkers
      group.on('dragstart', () => {
        dragAnchorRef.current = { x: group.x(), y: group.y() };
        otherDragTargets.current = [];
        const pl = placedLayerRef.current;
        if (!pl) return;
        pl.getChildren().toArray().forEach(child => {
          const childId = child.getAttr('bunkerId') as string;
          if (childId && childId !== b.id && selectedIdsRef.current.includes(childId)) {
            const cg = child as Konva.Group;
            otherDragTargets.current.push({ group: cg, startX: cg.x(), startY: cg.y() });
          }
        });
      });

      group.on('dragmove', () => {
        if (!dragAnchorRef.current) return;
        const dx = group.x() - dragAnchorRef.current.x;
        const dy = group.y() - dragAnchorRef.current.y;

        const moveLabel = (id: string, gx: number, gy: number) => {
          const info = labelsByIdRef.current.get(id);
          if (!info) return;
          info.node.x(gx - 70); // 70 = labelW(140) / 2
          info.node.y(gy + info.halfH + 3);
        };
        moveLabel(b.id, group.x(), group.y());

        for (const { group: og, startX, startY } of otherDragTargets.current) {
          og.x(startX + dx);
          og.y(startY + dy);
          moveLabel(og.getAttr('bunkerId') as string, og.x(), og.y());
        }
        placedLayerRef.current?.batchDraw();
        labelsLayerRef.current?.batchDraw();
      });

      group.on('dragend', () => {
        const t2 = transformRef.current;
        const sf = snapFtRef.current;

        const commitPos = (gp: Konva.Group, id: string) => {
          let { xFt, zFt } = stageCoordsToField(gp.x(), gp.y(), t2);
          if (sf > 0) { xFt = snapToGrid(xFt, sf); zFt = snapToGrid(zFt, sf); }
          const c = clampField(xFt, zFt);
          onUpdateRef.current(id, { xFt: c.xFt, zFt: c.zFt });
        };

        commitPos(group, b.id);
        for (const { group: og } of otherDragTargets.current) {
          commitPos(og, og.getAttr('bunkerId') as string);
        }
        dragAnchorRef.current  = null;
        otherDragTargets.current = [];
      });

      layer.add(group);
    }

    layer.batchDraw();

    // ── Labels layer: always-horizontal text, one per placed bunker ─────────
    const labelsLayer = labelsLayerRef.current;
    if (labelsLayer) {
      labelsLayer.destroyChildren();
      labelsByIdRef.current.clear();
      const fontSize = Math.max(10, Math.min(14, ppf * 1.5));
      const labelW   = 140;

      for (const b of placedBunkers) {
        const def = bunkerDefs[b.type];
        if (!def) continue;
        const { x, y } = fieldToStageCoords(b.xFt, b.zFt, t);
        const sprite    = def.sprites?.[tileset];
        const resolvedW = sprite?.widthFt ?? def.widthFt;
        const resolvedD = sprite?.depthFt ?? def.depthFt;
        const pxW = resolvedW * ppf;
        const pxH = resolvedD * ppf;

        // Exact downward visual extent of the rotated rectangle, so the label
        // always sits just below the visible edge regardless of rotation angle.
        const θ = b.rotationDeg * Math.PI / 180;
        const visualHalfHeight = Math.abs(pxW / 2 * Math.sin(θ)) + Math.abs(pxH / 2 * Math.cos(θ));

        const textNode = new Konva.Text({
          x: x - labelW / 2,
          y: y + visualHalfHeight + 3,
          width: labelW,
          text: bunkerDisplayName(def, tileset),
          fontSize,
          fontStyle: 'bold',
          fill: '#ffffff',
          align: 'center',
          listening: false,
          shadowColor: '#000000',
          shadowBlur: 3,
          shadowOpacity: 0.9,
          shadowOffsetX: 0,
          shadowOffsetY: 1,
        });
        labelsLayer.add(textNode);
        labelsByIdRef.current.set(b.id, { node: textNode, halfH: visualHalfHeight });
      }
      labelsLayer.batchDraw();
    }
  }, [placedBunkers, transform, bunkerDefs, selectedIds, stage, tileset, spriteImages]);

  // ── Recreate ghost(s) when type or transform changes ────────────────────────
  useEffect(() => {
    const layer = ghostLayerRef.current;
    if (!layer) return;

    // Remove only ghost groups, not the selection rect
    layer.getChildren().toArray().forEach(child => {
      if (child !== selRectRef.current) child.destroy();
    });
    ghostGroupRef.current  = null;
    mirrorGhostRef.current = null;

    if (selectedType) {
      const def = bunkerDefs[selectedType];
      if (def) {
        // On fresh selection, seed the rotation from the per-orientation default.
        // If the user already manually rotated this type, keep their value.
        if (selectedType !== prevSelectedTypeRef.current) {
          const defaultRot = orientationRef.current === 'horizontal'
            ? (HORIZONTAL_DEFAULT_ROT[selectedType] ?? 0)
            : 0;
          rotByTypeRef.current[selectedType] = defaultRot;
          prevSelectedTypeRef.current = selectedType;
        }
        const rot      = rotByTypeRef.current[selectedType] ?? 0;
        const sprite   = def.sprites?.[tileset];
        const resolvedW = sprite?.widthFt ?? def.widthFt;
        const resolvedD = sprite?.depthFt ?? def.depthFt;
        const img      = spriteImages[selectedType];

        const mirrorRot = def.taperEdge
          ? rot
          : mirrorRotation(rot, orientationRef.current);

        const primary = buildGhostGroup(resolvedW, resolvedD, rot,       transform.basePxPerFoot, img, 0.6);
        const mirror  = buildGhostGroup(resolvedW, resolvedD, mirrorRot, transform.basePxPerFoot, img, 0.4);

        // Taper indicator on ghost groups — same dual-bar style as placed bunkers
        if (def.taperEdge) {
          const ppf    = transform.basePxPerFoot;
          const pxW    = resolvedW * ppf;
          const pxH    = resolvedD * ppf;
          const sw     = Math.max(3, Math.min(10, ppf * 0.7));
          const shortH = pxH * 0.4;
          const taperX    = def.taperEdge === 'left' ? -pxW / 2 : pxW / 2 - sw;
          const nonTaperX = def.taperEdge === 'left' ? pxW / 2 - sw : -pxW / 2;
          const base = { fill: '#ffaa00', opacity: 0.85, listening: false, width: sw };
          for (const g of [primary, mirror]) {
            g.add(new Konva.Rect({ ...base, x: nonTaperX, y: -pxH / 2, height: pxH }));
            g.add(new Konva.Rect({ ...base, x: taperX,    y: -shortH / 2, height: shortH }));
          }
        }

        ghostGroupRef.current = primary;
        layer.add(primary);
        mirror.visible(false);
        mirrorGhostRef.current = mirror;
        layer.add(mirror);
      }
    }
    layer.batchDraw();
  }, [selectedType, transform, bunkerDefs, tileset, spriteImages]);

  return null;
};
